import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Edit, Settings, BarChart3 } from "lucide-react";
import {
  useMeterReadingFormatDetail,
  usePatchMeterReadingFormat,
} from "../../hooks";
import { QueryClient } from "@tanstack/react-query";
import { queryClient } from "@shared/api/queries/queryClient";
import { convertKeysToSnakeCase } from "@shared/utils/caseConverter";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/ui/use-toast";
const FormatDetailView = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const remoteUtilityId = useRemoteUtilityId();
  const { data: formatDetail } = useMeterReadingFormatDetail(
    id,
    remoteUtilityId
  );
  const { mutate: updateFormat } = usePatchMeterReadingFormat();

  const fields = Object.values(formatDetail?.configurationJson ?? {});
  console.log("debug config", fields);

  const format = {
    utilityService: formatDetail?.utilityService,
    name: formatDetail?.name,
    readType: formatDetail?.meterTypeDisplay,
    isActive: formatDetail?.isActive,
    fields: fields,
    createdDate: formatDetail?.createdDate, // Initialize with a Date object
    lastModified: formatDetail?.lastModifiedDate, // Initialize with a Date object
    performanceStats: formatDetail?.performanceMatrix,
  };
  console.log("Format Detail:", format);

  if (!id) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Format Not Found</h1>
          <Button onClick={() => navigate("/formats")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Formats
          </Button>
        </div>
      </div>
    );
  }

  const getUtilityColor = (type: string) => {
    switch (type) {
      case "water":
        return "bg-blue-100 text-blue-700 border-blue-300";
      case "electric":
        return "bg-yellow-100 text-yellow-700 border-yellow-300";
      case "gas":
        return "bg-orange-100 text-orange-700 border-orange-300";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  const getReadTypeColor = (type: string) => {
    switch (type) {
      case "manual":
        return "bg-blue-100 text-blue-700";
      case "photo":
        return "bg-green-100 text-green-700";
      case "smart":
        return "bg-purple-100 text-purple-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const handleDeactivate = () => {
    const payload = {
      remote_utility_id: Number(remoteUtilityId),
      is_active: !formatDetail.isActive, // toggle the value
    };
    updateFormat(
      { id: Number(id), payload },
      {
        onSuccess: async () => {
          toast({
            title: "Success",
            description: "Format Updated Successfully",
            variant: "default",
          });
          await queryClient.invalidateQueries({
            queryKey: ["mx", "meter-reading-format-detail"],
          });
          navigate(-1);
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Something went wrong.",
            variant: "destructive",
          });
        },
      }
    );
  };
  return (
    <div className="container mx-auto px-6 py-4">
      {/* Header Section */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">{format.name}</h1>
            <p className="text-gray-600">Format Details and Configuration</p>
          </div>
        </div>
        <Button
          className="flex items-center gap-2"
          onClick={() =>
            navigate(`/mx/settings/meterread-format/edit/${formatDetail?.id}`)
          }
        >
          <Edit className="h-4 w-4" />
          Edit Format
        </Button>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
        {/* Format Configuration - Takes up more space */}
        <div className="xl:col-span-3 space-y-6">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Format Configuration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">
                    Utility Service
                  </label>
                  <Badge
                    className={`${getUtilityColor(
                      format.utilityService
                    )} capitalize`}
                  >
                    {format.utilityService}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">
                    Read Type
                  </label>
                  <Badge
                    className={`${getReadTypeColor(
                      format.readType
                    )} capitalize`}
                  >
                    {format.readType}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">
                    Status
                  </label>
                  <Badge variant={format.isActive ? "default" : "secondary"}>
                    {format.isActive ? "Active" : "Draft"}
                  </Badge>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">
                    Total Fields
                  </label>
                  <div className="text-2xl font-bold">{fields?.length}</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-1 block">
                    Created On
                  </label>
                  <div className="text-lg">{format.createdDate}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-1 block">
                    Last Modified
                  </label>
                  <div className="text-lg">{format.lastModified}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Fields List */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle>Format Fields ({format.fields.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {format.fields.map((field, index) => (
                  <div
                    key={field.sequenceId}
                    className="flex items-center justify-between p-4 border rounded-lg bg-gray-50/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-white border rounded-full flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </div>
                      <div>
                        <div className="flex items-center flex-wrap gap-2">
                          <span className="font-medium">{field.fieldName}</span>
                        </div>
                        <div className="text-sm text-gray-500 capitalize mt-1">
                          {field.fieldType} • {field.inputType}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {field.required && (
                        <Badge variant="destructive" className="text-xs">
                          Required
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs capitalize">
                        {field.displayType}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Performance Stats Sidebar */}
        <div className="xl:col-span-1 space-y-6">
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Performance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {format.performanceStats && (
                <>
                  <div>
                    <label className="text-sm font-medium text-gray-500 mb-1 block">
                      Avg. Time
                    </label>
                    <div className="text-2xl font-bold text-blue-600">
                      {format.performanceStats.avgHours ?? 0}s
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500 mb-1 block">
                      Success Rate
                    </label>
                    <div className="text-2xl font-bold text-green-600">
                      {format.performanceStats.successRate}%
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500 mb-1 block">
                      Usage Count
                    </label>
                    <div className="text-2xl font-bold text-gray-800">
                      {format.performanceStats.totalUsage}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-4">
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start text-sm"
                onClick={() =>
                  navigate(
                    `/mx/settings/meterread-format/edit/${formatDetail?.id}`
                  )
                }
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Format
              </Button>
              <Button
                onClick={handleDeactivate}
                className={`w-full justify-start text-sm ${
                  !formatDetail?.isActive
                    ? "bg-green-600 hover:bg-green-700 text-white"
                    : ""
                }`}
                variant={formatDetail?.isActive ? "destructive" : "default"}
              >
                {formatDetail?.isActive ? "Deactivate" : "Reactivate"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FormatDetailView;
